import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import { ChevronRight, Home, AlertTriangle, Target, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';

const SITE_URL = 'https://www.practice4sat.com';

const questionTypes = [
  {
    number: 1,
    title: "Solve a System Efficiently",
    strategyName: "Eliminate in one move",
    strategySteps: [
      "Look for coefficients that can cancel immediately",
      "Multiply once (if needed) to align coefficients",
      "Add or subtract equations",
      "Solve and back-substitute"
    ],
    commonTrap: "Students try substitution when elimination is much faster",
    wrongApproach: null,
    example: {
      problem: "Solve for $x$ and $y$:",
      equation: "$$2x + 3y = 13$$ $$2x - y = 5$$",
      steps: [
        "$(2x + 3y) - (2x - y) = 13 - 5$",
        "$4y = 8 \\Rightarrow y = 2$",
        "Substitute into second equation:",
        "$2x - 2 = 5 \\Rightarrow 2x = 7 \\Rightarrow x = \\dfrac{7}{2}$"
      ]
    },
    practice: {
      problem: "Solve for $x$ and $y$:",
      equation: "$$3x + 2y = 12$$ $$3x - y = 3$$",
      answer: "$y = 3, \\quad x = 2$",
      explanation: [
        "Eliminate $x$ immediately \u2192 fastest path"
      ]
    },
    keyTakeaway: "Always look for quick elimination before substitution."
  },
  {
    number: 2,
    title: "No Solution vs Infinitely Many Solutions",
    strategyName: "Compare structure quickly",
    strategySteps: [
      "Rewrite equations in the same form",
      "Same left side, different constants \u2192 no solution",
      "Same equation \u2192 infinitely many solutions"
    ],
    commonTrap: "Students try solving fully instead of recognizing patterns",
    wrongApproach: null,
    example: {
      problem: "Determine whether the system has no solution or infinitely many solutions:",
      equation: "$$2x + 4y = 8$$ $$x + 2y = 5$$",
      steps: [
        "Multiply second equation by 2:",
        "$2x + 4y = 10$",
        "Compare:",
        "$2x + 4y = 8$",
        "$2x + 4y = 10$",
        "Same left side, different constants \u2192 no solution"
      ]
    },
    practice: {
      problem: "Determine whether the system has no solution or infinitely many solutions:",
      equation: "$$3x + 6y = 9$$ $$x + 2y = 3$$",
      answer: "Infinitely many solutions",
      explanation: [
        "$3(x + 2y = 3) \\Rightarrow 3x + 6y = 9$",
        "Same equation \u2192 infinite solutions"
      ]
    },
    keyTakeaway: "Check structure first \u2014 don't over-solve."
  },
  {
    number: 3,
    title: "Systems in Word Problems",
    strategyName: "Define variables carefully",
    strategySteps: [
      "Assign variables clearly",
      "Translate each statement into an equation",
      "Solve using elimination or substitution"
    ],
    commonTrap: "Incorrect equation setup",
    wrongApproach: "$x + 5 = 2y$ (wrong translation)",
    example: {
      problem: "The sum of two numbers is 20. One number is 4 more than the other. Find the numbers.",
      equation: null,
      steps: [
        "Let $x$ = larger number, $y$ = smaller number",
        "Equations: $x + y = 20$ and $x = y + 4$",
        "Substitute: $(y + 4) + y = 20$",
        "$2y + 4 = 20 \\Rightarrow 2y = 16 \\Rightarrow y = 8$",
        "$x = 12$"
      ]
    },
    practice: {
      problem: "The sum of two numbers is 30. One number is 6 less than the other.",
      equation: null,
      answer: "$y = 12, \\quad x = 18$",
      explanation: [
        "Translate correctly \u2192 then solve"
      ]
    },
    keyTakeaway: "Most mistakes come from incorrect equation setup."
  },
  {
    number: 4,
    title: "Systems Hidden in Expressions",
    strategyName: "Treat expressions as variables",
    strategySteps: [
      "Assign variables to repeating expressions",
      "Rewrite as a system",
      "Solve normally"
    ],
    commonTrap: "Students expand everything instead of simplifying structure",
    wrongApproach: null,
    example: {
      problem: "Find $a$:",
      equation: "$$a + b = 10$$ $$2a + b = 16$$",
      steps: [
        "$(2a + b) - (a + b) = 16 - 10$",
        "$a = 6$"
      ]
    },
    practice: {
      problem: "Find $x$:",
      equation: "$$x + y = 7$$ $$2x + y = 11$$",
      answer: "$x = 4$",
      explanation: [
        "Subtract equations to eliminate $y$"
      ]
    },
    keyTakeaway: "If expressions repeat, treat them like variables."
  },
  {
    number: 5,
    title: "Systems with Parameters",
    strategyName: "Solve in terms of the parameter",
    strategySteps: [
      "Solve normally but keep the parameter",
      "Combine equations strategically",
      "Avoid plugging numbers too early"
    ],
    commonTrap: "Students plug random values instead of solving algebraically",
    wrongApproach: null,
    example: {
      problem: "Find $x$ in terms of $k$:",
      equation: "$$x + y = k$$ $$x - y = 4$$",
      steps: [
        "Add equations:",
        "$2x = k + 4$",
        "$x = \\dfrac{k + 4}{2}$"
      ]
    },
    practice: {
      problem: "Find $x$:",
      equation: "$$x + y = k$$ $$2x - y = 3$$",
      answer: "$x = \\dfrac{k + 3}{3}$",
      explanation: [
        "Add equations:",
        "$3x = k + 3$",
        "$x = \\dfrac{k + 3}{3}$"
      ]
    },
    keyTakeaway: "Keep everything symbolic until the end."
  }
];

function PracticeReveal({ practice, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`practice-${idx}`} style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '1.25rem', marginTop: '1rem' }}>
      <h4 style={{ fontWeight: 600, fontSize: '1.05rem', color: '#166534', margin: '0 0 .75rem 0', fontFamily: "'Arial', sans-serif" }}>
        Try It Yourself
      </h4>
      {practice.problem && <p style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 .5rem 0', fontFamily: "'Arial', sans-serif" }}>{practice.problem}</p>}
      {practice.equation && <p style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 .75rem 0', fontFamily: "'Arial', sans-serif" }}>{practice.equation}</p>}
      {practice.wrongApproach && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '.75rem 1rem', marginBottom: '.75rem' }}>
          <p style={{ margin: 0, fontSize: '.9rem', color: '#991b1b', fontFamily: "'Arial', sans-serif" }}>
            <strong>What Most Students Do (Wrong):</strong> {practice.wrongApproach}
          </p>
        </div>
      )}
      <button
        data-testid={`reveal-answer-${idx}`}
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: '.5rem',
          background: '#166534', color: '#fff', border: 'none', borderRadius: 6,
          padding: '.5rem 1rem', cursor: 'pointer', fontSize: '.95rem', fontWeight: 500,
          fontFamily: "'Arial', sans-serif"
        }}
      >
        {open ? 'Hide Answer' : 'Show Answer'}
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div style={{ marginTop: '.75rem', padding: '.75rem', background: '#fff', borderRadius: 8, border: '1px solid #d1fae5' }}>
          <p style={{ fontWeight: 600, color: '#166534', margin: '0 0 .5rem 0', fontFamily: "'Arial', sans-serif" }}>
            Answer: {practice.answer}
          </p>
          <p style={{ fontWeight: 600, fontSize: '.95rem', color: '#374151', margin: '0 0 .25rem 0', fontFamily: "'Arial', sans-serif" }}>Quick Explanation:</p>
          {practice.explanation.map((step, i) => (
            <p key={`step-${i}`} style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 .25rem 0', paddingLeft: '.5rem', fontFamily: "'Arial', sans-serif" }}>{step}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionTypeSection({ qt, idx }) {
  const bg = idx % 2 === 0 ? '#f8f9fa' : '#ffffff';
  return (
    <section
      aria-labelledby={`qt-${qt.number}`}
      data-testid={`question-type-${qt.number}`}
      style={{ background: bg, padding: '2rem 0' }}
    >
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 2rem' }}>
        <h2 id={`qt-${qt.number}`} style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0030b3', margin: '0 0 1.25rem 0', fontFamily: "'Arial', sans-serif" }}>
          {"Question Type " + qt.number + ": " + qt.title}
        </h2>

        {/* Strategy */}
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1e40af', margin: '0 0 .5rem 0', display: 'flex', alignItems: 'center', gap: '.4rem', fontFamily: "'Arial', sans-serif" }}>
            <Target size={16} /> {"Strategy: " + qt.strategyName}
          </h3>
          <ol style={{ margin: 0, paddingLeft: '1.25rem' }}>
            {qt.strategySteps.map((s, i) => (
              <li key={`strategy-${i}`} style={{ color: '#1e3a5f', lineHeight: 1.6, fontFamily: "'Arial', sans-serif" }}>{s}</li>
            ))}
          </ol>
        </div>

        {/* Common Trap */}
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#991b1b', margin: '0 0 .25rem 0', display: 'flex', alignItems: 'center', gap: '.4rem', fontFamily: "'Arial', sans-serif" }}>
            <AlertTriangle size={16} /> {"Common Trap: " + qt.commonTrap}
          </h3>
          {qt.wrongApproach && (
            <p style={{ margin: '.5rem 0 0 0', fontSize: '.95rem', color: '#7f1d1d', fontFamily: "'Arial', sans-serif" }}>
              <strong>What Most Students Do (Wrong):</strong> {qt.wrongApproach}
            </p>
          )}
        </div>

        {/* Example */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.25rem', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 600, color: '#374151', margin: '0 0 .75rem 0', fontFamily: "'Arial', sans-serif" }}>Example</h3>
          {qt.example.problem && <p style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 .25rem 0', fontFamily: "'Arial', sans-serif" }}>{qt.example.problem}</p>}
          {qt.example.equation && <p style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 .75rem 0', fontFamily: "'Arial', sans-serif" }}>{qt.example.equation}</p>}
          <h4 style={{ fontSize: '.95rem', fontWeight: 600, color: '#374151', margin: '0 0 .5rem 0', fontFamily: "'Arial', sans-serif" }}>Solution</h4>
          {qt.example.steps.map((step, i) => (
            <p key={`step-${i}`} style={{ color: '#374151', lineHeight: 1.6, margin: '0 0 .25rem 0', paddingLeft: '.5rem', fontFamily: "'Arial', sans-serif" }}>{step}</p>
          ))}
        </div>

        {/* Practice */}
        <PracticeReveal practice={qt.practice} idx={qt.number} />

        {/* Key Takeaway */}
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1rem' }}>
          <p style={{ margin: 0, display: 'flex', alignItems: 'flex-start', gap: '.4rem', fontFamily: "'Arial', sans-serif", color: '#92400e', fontWeight: 500, lineHeight: 1.5 }}>
            <Lightbulb size={16} style={{ marginTop: 3, flexShrink: 0 }} />
            <span><strong>Key Takeaway:</strong> {qt.keyTakeaway}</span>
          </p>
        </div>
      </div>
    </section>
  );
}

export default function SystemsOfEquations() {
  const contentRef = useRef(null);

  useEffect(() => {
    if (window.loadMathJax) window.loadMathJax();
    const timer = setInterval(() => {
      if (window.MathJax?.typesetPromise && contentRef.current) {
        window.MathJax.typesetPromise([contentRef.current]).catch(() => {});
        clearInterval(timer);
      }
    }, 200);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (window.MathJax?.typesetPromise && contentRef.current) {
        window.MathJax.typesetPromise([contentRef.current]).catch(() => {});
      }
    });
    if (contentRef.current) {
      observer.observe(contentRef.current, { childList: true, subtree: true });
    }
    return () => observer.disconnect();
  }, []);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': 'Master the Most Common SAT Systems of Equations Questions',
    'description': 'A comprehensive guide to solving SAT systems of equations faster with strategies, worked examples, and practice problems.',
    'author': { '@type': 'Organization', 'name': 'Practice4SAT', 'url': SITE_URL },
    'publisher': {
      '@type': 'Organization',
      'name': 'Practice4SAT',
      'url': SITE_URL,
      'logo': { '@type': 'ImageObject', 'url': 'https://www.practice4sat.com/assets/images/logo.png' }
    },
    'datePublished': '2026-04-23',
    'mainEntityOfPage': `${SITE_URL}/sat-tricks/systems-of-equations`,
    'image': 'https://www.practice4sat.com/assets/images/logo.png'
  };

  return (
    <div className="App">
      <SEOHead
        title="Systems of Equations - SAT Math Tricks & Strategies"
        description="Master the most common SAT systems of equations questions. Learn strategies for elimination, no solution vs infinitely many solutions, word problems, hidden expressions, and parameter systems."
        canonical="/sat-tricks/systems-of-equations"
        keywords="SAT systems of equations, SAT elimination method, SAT no solution infinitely many solutions, SAT algebra tricks, SAT math strategies, SAT word problems systems, SAT parameter equations"
        structuredData={structuredData}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'SAT Tricks & Strategies', url: '/sat-tricks' },
          { name: 'Systems of Equations' }
        ]}
      />

      <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column' }}>
        <Header variant="public" />

        <main style={{ flex: 1 }} ref={contentRef}>
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" data-testid="breadcrumb-nav" style={{ padding: '1rem 2rem 0', maxWidth: 1400, margin: '0 auto' }}>
            <ol style={{ display: 'flex', alignItems: 'center', gap: '.4rem', listStyle: 'none', padding: 0, margin: 0, fontSize: '.9rem', fontFamily: "'Arial', sans-serif" }}>
              <li>
                <Link to="/" style={{ color: '#6b7280', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '.25rem' }}>
                  <Home size={14} aria-hidden="true" /><span>Home</span>
                </Link>
              </li>
              <li aria-hidden="true" style={{ color: '#9ca3af' }}><ChevronRight size={14} /></li>
              <li>
                <Link to="/sat-tricks" style={{ color: '#6b7280', textDecoration: 'none' }}>SAT Tricks & Strategies</Link>
              </li>
              <li aria-hidden="true" style={{ color: '#9ca3af' }}><ChevronRight size={14} /></li>
              <li><span aria-current="page" style={{ color: '#0030b3', fontWeight: 600 }}>Systems of Equations</span></li>
            </ol>
          </nav>

          <article>
            {/* Title & Intro */}
            <div style={{ padding: '2rem 2rem 1rem', maxWidth: 1400, margin: '0 auto' }}>
              <h1 data-testid="subtopic-title" style={{ fontSize: '1.5rem', fontWeight: 600, textAlign: 'center', marginTop: 0, marginBottom: '.75rem', color: '#0030b3' }}>
                Master the Most Common SAT Systems of Equations Questions
              </h1>
              <p style={{ color: '#374151', lineHeight: 1.6, marginBottom: '1rem', fontFamily: "'Arial', sans-serif" }}>
                Systems of equations are one of the most frequently tested algebra topics on the SAT.
                This guide focuses on the exact question types you'll see on the real exam — and shows you how to solve them faster, avoid traps, and recognize patterns instantly.
              </p>
            </div>

            {/* Question Type Sections */}
            {questionTypes.map((qt, i) => (
              <QuestionTypeSection key={qt.number} qt={qt} idx={i} />
            ))}

            {/* Internal Links */}
            <aside style={{ maxWidth: 1400, margin: '2rem auto', padding: '1.5rem 2rem' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#374151', marginBottom: '.75rem', fontFamily: "'Arial', sans-serif" }}>
                Ready to Improve Your SAT Score?
              </h2>
              <nav aria-label="Related SAT resources" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                <Link to="/quick-quiz" data-testid="link-diagnostic-quiz" style={{ color: '#0030b3', textDecoration: 'underline', fontSize: '.95rem', fontFamily: "'Arial', sans-serif" }}>
                  Take the Free SAT Diagnostic Quiz
                </Link>
                <a href="/#pricing" data-testid="link-study-plan" style={{ color: '#0030b3', textDecoration: 'underline', fontSize: '.95rem', fontFamily: "'Arial', sans-serif" }}>
                  Get Your Personalized Study Plan
                </a>
              </nav>
            </aside>
          </article>
        </main>

        <Footer />
      </div>

      <style>{`
        @media (min-width: 768px) {
          .desktop-nav { display: flex !important; }
          .mobile-menu-btn { display: none !important; }
        }
        @media (max-width: 767px) {
          .desktop-nav { display: none !important; }
        }
        .footer-link:hover { color: #91ccff !important; }
      `}</style>
    </div>
  );
}
